import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { DealDto } from '@78/shared';
import { graceOf, isInDefault, payBefore, refundPercentOf, type DealTerms } from '@78/core';
import { fetchAwaitingDeals, fetchWalletDeals } from '../lib/api.ts';
import { getNftOwedTo, getNftOwner } from '../lib/chain.ts';
import { CURRENCY } from '@78/i18n';
import { useActions } from '../lib/actions.ts';
import { GasNote } from '../components/GasNote.tsx';
import { useErrorText } from '../lib/errors.ts';
import { reportFailure } from '../lib/report.ts';
import { sameAddress, shortAddress } from '../lib/collections.ts';
import { useFmt } from '../lib/format.tsx';
import { GiftArt } from '../components/GiftArt.tsx';
import { resolveInvitation } from '../lib/referral.ts';
import { Sheet } from '../components/Sheet.tsx';

/** Сколько ждать сеть, прежде чем спрашивать индексер заново. */
const NETWORK_WAIT_MS = 6000;

/** Цену позиции человек называет целыми монетами, контракт — нанотонами. */
const TON_UNIT = 1_000_000_000n;

function toTerms(d: DealDto): DealTerms {
  return {
    totalPrice: BigInt(d.terms.totalPrice),
    upfront: BigInt(d.terms.upfront),
    deferFee: BigInt(d.terms.deferFee),
    period: d.terms.period,
    nPayments: d.terms.nPayments,
    refundTier: d.terms.refundTier,
    maxDeferrals: d.terms.maxDeferrals,
    feeBps: d.terms.feeBps,
    deadline: d.terms.deadline,
  };
}

/** Сколько платежей уже сделано — по внесённому сверх взноса. */
function stepsDone(d: DealDto, terms: DealTerms): number {
  const installment = (terms.totalPrice - terms.upfront) / BigInt(terms.nPayments);
  if (installment <= 0n) return 0;
  const paid = BigInt(d.progress.paidTotal) - terms.upfront;
  return paid <= 0n ? 0 : Math.min(Number(paid / installment), terms.nPayments);
}

/**
 * Действие в блокчейне: занятость, ошибка и ожидание сети.
 *
 * Ожидание показывается явно. Без него человек подтверждает перевод в
 * кошельке, возвращается — и видит ровно тот же экран: ничего не изменилось,
 * потому что сеть ещё не подтвердила. Молчание читается как «не сработало»,
 * и человек платит второй раз.
 */
function useChainAction() {
  const qc = useQueryClient();
  const errorText = useErrorText();
  const [busy, setBusy] = useState<string | null>(null);
  const [waiting, setWaiting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const run = async (name: string, fn: () => Promise<unknown>) => {
    setBusy(name);
    setErr(null);
    try {
      await fn();
      setWaiting(true);
      setTimeout(() => {
        void qc.invalidateQueries({ queryKey: ['myDeals'] });
        setWaiting(false);
      }, NETWORK_WAIT_MS);
    } catch (e) {
      // Всё это — действия кошельком: их провал виден только здесь.
      reportFailure(name, e);
      const text = errorText(e);
      if (text.length > 0) setErr(text);
    } finally {
      setBusy(null);
    }
  };

  return { busy, waiting, err, run };
}

function Waiting({ show }: { show: boolean }) {
  const { t } = useTranslation();
  if (!show) return null;
  return (
    <div className="col" style={{ gap: 2 }}>
      <span className="label lime">{t('state.sent_waiting')}</span>
      <span className="text" style={{ fontSize: 11 }}>{t('state.sent_waiting_hint')}</span>
    </div>
  );
}

/** Шкала шагов: сколько платежей закрыто. Приём макета — точки-полоски. */
function Dots({ done, total }: { done: number; total: number }) {
  return (
    <div style={{ display: 'flex', gap: 5 }}>
      {Array.from({ length: total + 1 }, (_, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            height: 6,
            borderRadius: 3,
            background: i <= done ? 'var(--lime)' : 'var(--line)',
          }}
        />
      ))}
    </div>
  );
}

/** Изображение гифта — маленькое, как в карточках сделок. */
function Thumb({ image = null }: { image?: string | null }) {
  return <GiftArt image={image} size={52} />;
}

/**
 * Зависший гифт (§9.5).
 *
 * Появляется, когда перевод NFT не приняли и он вернулся баунсом: гифт
 * числится за контрактом и ждёт повторной отправки. Денег в этом разделе
 * больше нет — выплаты уходят родной валютой невозвратными сообщениями и
 * доходят всегда, даже на неразвёрнутый кошелёк.
 *
 * Дёрнуть повтор может кто угодно: адресат записан в самой сделке, и
 * выбрать его нельзя.
 */
function StuckPayout({ deal }: { deal: DealDto }) {
  const { t } = useTranslation();
  const actions = useActions();
  const { busy, waiting, err, run } = useChainAction();

  const { data: owedTo } = useQuery({
    queryKey: ['nftOwed', deal.address],
    queryFn: () => getNftOwedTo(deal.address),
    enabled: actions.connected,
  });

  if (owedTo === undefined || owedTo === null) return null;

  return (
    <div className="col" style={{ gap: 14 }}>
      <div>
        <div className="label" style={{ fontSize: 9, letterSpacing: '0.18em' }}>
          STUCK GIFT · {t('state.stuck_rare')}
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.2, marginTop: 6 }}>
          {t('state.stuck_hero')}
        </div>
        <div className="text" style={{ fontSize: 13.5, lineHeight: 1.55, marginTop: 6 }}>
          {t('state.stuck_note')}
        </div>
      </div>

      {/* Пунктиром и лаймом: гифт не потерян, но и не придёт сам. */}
      <div
        className="col"
        style={{
          padding: 18,
          borderRadius: 18,
          border: '1px dashed var(--lime)',
          background: 'var(--sf)',
          alignItems: 'center',
          gap: 3,
        }}
      >
        <div className="label">{t('deals.waiting_in_contract')}</div>
        <div className="mono" style={{ fontSize: 13 }}>{shortAddress(owedTo)}</div>
      </div>

      {err !== null && <div className="card card-dng text">{err}</div>}
      <Waiting show={waiting} />

      <button
        className="btn"
        disabled={busy !== null || !actions.connected}
        onClick={() => void run('claim', () => actions.withdrawOwed(deal.address))}
      >
        {busy === 'claim' ? '…' : t('state.claim')}
      </button>
    </div>
  );
}

/**
 * Терминальная сделка: выкуплена или сорвана.
 *
 * В макете это полноэкранная сцена, а не строчка в списке, и правильно:
 * человек открывает её один раз, чтобы узнать исход, и исход должен
 * читаться с первого взгляда — знак, заголовок, потом цифры.
 */
function SettledDeal({ deal }: { deal: DealDto }) {
  const { t } = useTranslation();
  const { money } = useFmt();
  const terms = toTerms(deal);
  const paid = BigInt(deal.progress.paidTotal);
  const bought = paid >= terms.totalPrice;
  const refunded = bought ? 0n : BigInt(deal.progress.retained);

  return (
    <>
      <div className="col" style={{ alignItems: 'center', textAlign: 'center', gap: 18, padding: '12px 4px' }}>
        <div style={{ border: `2px solid ${bought ? 'var(--lime)' : 'var(--dng)'}`, borderRadius: 28, padding: 2 }}>
          <GiftArt image={deal.nftImage} size={110} radius={26} />
        </div>
        <div>
          <div
            className="mono"
            style={{
              fontSize: 9,
              letterSpacing: '0.2em',
              color: bought ? 'var(--lime-text)' : 'var(--dng)',
            }}
          >
            {bought ? 'DEAL CLOSED' : 'DEAL DEFAULTED'}
          </div>
          <div style={{ fontSize: 25, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.2, marginTop: 8 }}>
            {bought ? t('state.closed_hero') : t('state.failed_hero')}
          </div>
          <div className="text" style={{ fontSize: 13.5, lineHeight: 1.55, marginTop: 8 }}>
            {bought ? t('state.closed_note') : t('state.failed_note')}
          </div>
        </div>

        <div className="card col" style={{ width: '100%', textAlign: 'left', gap: 9 }}>
          <div className="row">
            <span className="text">{t('state.paid_total')}</span>
            <span className="mono num" style={{ fontWeight: 700 }}>{money(paid)}</span>
          </div>
          <div className="row">
            <span className="text">{t('deal.deal_total')}</span>
            <span className="mono num" style={{ fontWeight: 700 }}>{money(terms.totalPrice)}</span>
          </div>
          {!bought && (
            <>
              <div className="row">
                <span className="text">{t('state.refunded')}</span>
                <span className="mono num lime" style={{ fontWeight: 700 }}>{money(refunded)}</span>
              </div>
              <div className="row">
                <span className="text">{t('state.forfeited')}</span>
                <span className="mono num danger" style={{ fontWeight: 700 }}>{money(paid - refunded)}</span>
              </div>
            </>
          )}
          <div className="row">
            <span className="text">{t('positions.in_escrow')}</span>
            <span className="mono" style={{ fontSize: 11 }}>{shortAddress(deal.nftAddress)}</span>
          </div>
        </div>
      </div>
      <StuckPayout deal={deal} />
    </>
  );
}

function BuyerDeal({ deal }: { deal: DealDto }) {
  const { t } = useTranslation();
  const { sum, money, dateTime, duration } = useFmt();
  const actions = useActions();
  const { busy, waiting, err, run } = useChainAction();
  const [confirmDefer, setConfirmDefer] = useState(false);

  if (deal.state === 3) return <SettledDeal deal={deal} />;

  const terms = toTerms(deal);
  const paid = BigInt(deal.progress.paidTotal);
  const now = Math.floor(Date.now() / 1000);
  const safeUntil = payBefore(terms, deal.progress.startTs, paid);
  const overdue = isInDefault(terms, deal.progress.startTs, paid, now);
  const atRisk = paid - BigInt(deal.progress.retained);
  const refund = BigInt(deal.progress.retained);
  const canDefer = deal.progress.deferralsUsed < terms.maxDeferrals && !overdue;
  const done = stepsDone(deal, terms);
  const installment = (terms.totalPrice - terms.upfront) / BigInt(terms.nPayments);

  return (
    <>
      {/* Срок — первым и крупно. При просрочке блок краснеет и пульсирует:
          дальше идёт льготный период, и цена промедления — вся сделка. */}
      <div
        className={overdue ? 'card card-dng col' : 'card col'}
        style={overdue ? { border: '1.5px solid var(--dng)', borderRadius: 20 } : { borderRadius: 20 }}
      >
        {overdue && (
          <div className="row" style={{ justifyContent: 'flex-start', gap: 8 }}>
            <span className="pulse" style={{ width: 7, height: 7, borderRadius: 99, background: 'var(--dng)' }} />
            <span className="label" style={{ color: 'var(--dng)' }}>
              {t('buyer.overdue_grace_period_running')}
            </span>
          </div>
        )}
        <div style={{ fontSize: 13, lineHeight: 1.45 }}>{t('buyer.safe_pay_until')}</div>
        <div className="big" style={{ lineHeight: 1.15 }}>
          {safeUntil === null ? '—' : dateTime(safeUntil)}
        </div>
        {safeUntil !== null && (
          <div className={overdue ? 'mono danger' : 'mono muted'} style={{ fontSize: 13, fontWeight: 700 }}>
            {t('state.left', { x: duration(safeUntil - now) })}
          </div>
        )}
        <div
          style={{
            fontSize: 12,
            lineHeight: 1.5,
            paddingTop: 7,
            borderTop: `1px solid ${overdue ? 'var(--dng)' : 'var(--line)'}`,
          }}
        >
          {t('deals.grace_note')} ({duration(graceOf(terms.refundTier))})
        </div>
      </div>

      {/* Сама сделка: что за гифт, на каком шаге и сколько платить сейчас. */}
      <div className="card col" style={{ gap: 13 }}>
        <div className="row" style={{ justifyContent: 'flex-start', gap: 12 }}>
          <Thumb image={deal.nftImage} />
          <div className="col" style={{ flex: 1, minWidth: 0, gap: 3 }}>
            <div className="title">{deal.nftName ?? shortAddress(deal.nftAddress)}</div>
            <div className="label" style={{ fontSize: 9.5, letterSpacing: 0 }}>
              {t('deals.payment_of', { n: Math.min(done + 1, terms.nPayments), total: terms.nPayments })} · {t('deals.in_escrow')}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="mono num" style={{ fontSize: 19, fontWeight: 700 }}>
              {money(deal.progress.amountDue)}
            </div>
            <div className="label label-xs">{t('buyer.due')}</div>
          </div>
        </div>

        <Dots done={done} total={terms.nPayments} />

        {/* К сумме приложится надбавка на обработку — человек увидит эту
            цифру в кошельке и должен понимать её до, а не после
            подтверждения. */}
        <GasNote kind="pay" />

        {err !== null && <div className="danger" style={{ fontSize: 12 }}>{err}</div>}
        <Waiting show={waiting} />

        <div style={{ display: 'flex', gap: 9 }}>
          <button
            className="btn btn-sm"
            style={{ flex: 1, padding: 13, borderRadius: 13, fontSize: 12.5 }}
            disabled={busy !== null || !actions.connected}
            onClick={() =>
              void run('pay', () =>
                actions.pay({
                  dealAddress: deal.address,
                  terms,
                  startTs: deal.progress.startTs,
                  exactAmount: BigInt(deal.progress.amountDue),
                }),
              )
            }
          >
            {busy === 'pay' ? '…' : `${t('action.pay')} ${sum(deal.progress.amountDue)}`}
          </button>
          <button
            className="btn btn-ghost btn-sm"
            style={{ flex: '0 0 auto', padding: '13px 14px', borderRadius: 13, width: 'auto', fontSize: 12.5 }}
            disabled={busy !== null || !actions.connected}
            onClick={() =>
              void run('ahead', () =>
                actions.pay({
                  dealAddress: deal.address,
                  terms,
                  startTs: deal.progress.startTs,
                  periodsAhead: 1,
                }),
              )
            }
          >
            {t('buyer.pay_ahead')}
          </button>
        </div>

        {/* Перенос — пунктиром: он возможен, но это не обычный шаг. */}
        <button
          className="row"
          style={{
            padding: 12,
            border: '1px dashed var(--line)',
            borderRadius: 13,
            background: 'transparent',
            color: 'var(--ink2)',
            font: 'inherit',
            fontSize: 12,
            fontWeight: 700,
            cursor: canDefer ? 'pointer' : 'default',
            opacity: canDefer ? 1 : 0.45,
          }}
          disabled={!canDefer || busy !== null || !actions.connected}
          onClick={() => setConfirmDefer(true)}
        >
          <span>{t('buyer.push_payment_week')}</span>
          <span className="mono">
            {sum(terms.deferFee)} ·{' '}
            {t('state.left', { x: terms.maxDeferrals - deal.progress.deferralsUsed })}
          </span>
        </button>
      </div>

      {/* Последствия — всегда на виду, а не за прокруткой. */}
      <div className="card col" style={{ gap: 10 }}>
        <div className="row" style={{ alignItems: 'baseline' }}>
          <span style={{ fontSize: 14.5, fontWeight: 800 }}>{t('buyer.if_i_stop_now')}</span>
          <span className="label">{t('buyer.always_visible')}</span>
        </div>
        <div className="stats2" style={{ gap: 9 }}>
          <div className="stat" style={{ padding: 12, borderRadius: 13 }}>
            <div className="label label-xs">{t('offer.get_back')}</div>
            <div className="mono num lime" style={{ fontSize: 21, fontWeight: 700, marginTop: 3 }}>
              {money(refund)}
            </div>
          </div>
          <div className="stat" style={{ padding: 12, borderRadius: 13 }}>
            <div className="label label-xs">{t('buyer.i_lose')}</div>
            <div className="mono num danger" style={{ fontSize: 21, fontWeight: 700, marginTop: 3 }}>
              {money(atRisk)}
            </div>
          </div>
        </div>
        <div className="text" style={{ fontSize: 11.5 }}>
          {t('deals.paid_of', { paid: money(paid), total: money(terms.totalPrice) })} ·{' '}
          {t('deals.left_payments', { n: terms.nPayments - done })}
        </div>
      </div>

      {/* График по шагам — из макета. Без него виден только ближайший
          платёж, и непонятно, сколько всего осталось. */}
      <div className="card col" style={{ gap: 2 }}>
        <div style={{ fontSize: 14.5, fontWeight: 800, marginBottom: 6 }}>{t('buyer.schedule')}</div>
        {Array.from({ length: terms.nPayments + 1 }, (_, step) => {
          const due = step === 0 ? deal.progress.startTs : deal.progress.startTs + step * terms.period;
          const amount = step === 0 ? terms.upfront : installment;
          const isPaid = step <= done;
          const late = step === done + 1 && overdue;
          return (
            <div key={step} className="row" style={{ padding: '10px 0', borderTop: '1px solid var(--line)' }}>
              <div className="row" style={{ justifyContent: 'flex-start', gap: 10, minWidth: 0 }}>
                <span
                  style={{
                    flex: '0 0 7px',
                    height: 7,
                    borderRadius: 99,
                    background: isPaid ? 'var(--lime)' : late ? 'var(--dng)' : 'var(--line)',
                  }}
                />
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 600 }}>
                    {step === 0 ? t('deal.down_payment') : t('deals.payment_n', { n: step })}
                  </div>
                  <div
                    className="mono"
                    style={{ fontSize: 9.5, marginTop: 2, color: late ? 'var(--dng)' : 'var(--ink3)' }}
                  >
                    {dateTime(due)} ·{' '}
                    {isPaid ? t('deals.schedule_paid') : late ? t('deals.schedule_overdue') : t('deals.schedule_ahead')}
                  </div>
                </div>
              </div>
              <span
                className="mono num"
                style={{ fontSize: 12.5, fontWeight: 700, color: isPaid ? 'var(--ink3)' : 'var(--ink)' }}
              >
                {money(amount)}
              </span>
            </div>
          );
        })}
      </div>

      {confirmDefer && (
        <Sheet
          title={t('confirm.defer')}
          confirmLabel={`${t('buyer.push_payment_week')} · ${sum(terms.deferFee)}`}
          busy={busy !== null}
          onClose={() => setConfirmDefer(false)}
          onConfirm={() => {
            setConfirmDefer(false);
            void run('defer', () =>
              actions.defer({
                dealAddress: deal.address,
                deferFee: terms.deferFee,
              }),
            );
          }}
        >
          <span className="text">{t('confirm.defer_body')}</span>
        </Sheet>
      )}
    </>
  );
}

/**
 * Передача позиции продавца.
 *
 * Контракт переносит право на оставшиеся платежи, но денег за позицию не
 * держит — расчёт между сторонами их дело. Поэтому здесь адрес, а не цена:
 * обещать в интерфейсе продажу, которой в контракте нет, нельзя.
 */
function TransferPosition({ deal }: { deal: DealDto }) {
  const { t } = useTranslation();
  const actions = useActions();
  const { busy, waiting, err, run } = useChainAction();
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [to, setTo] = useState('');

  if (!open) {
    return (
      <button className="btn btn-ghost btn-sm" onClick={() => setOpen(true)}>
        {t('seller.transfer_position')}
      </button>
    );
  }

  return (
    <div className="col" style={{ gap: 8 }}>
      <span className="text" style={{ fontSize: 11.5 }}>{t('seller.transfer_position_hint')}</span>
      <input
        className="field"
        value={to}
        placeholder={t('seller.new_owner_address')}
        onChange={(e) => setTo(e.target.value.trim())}
      />
      {err !== null && <div className="danger" style={{ fontSize: 12 }}>{err}</div>}
      <Waiting show={waiting} />

      {/* Кошелёк подтвердит перевод, но не объяснит, что уходит: адрес там
          выглядит так же, как в поле ввода, а опечатка в нём означает, что
          право на все платежи уходит незнакомцу и без возврата. */}
      {confirming && (
        <Sheet
          title={t('confirm.transfer_position')}
          confirmLabel={t('seller.transfer_position')}
          danger
          busy={busy !== null}
          onClose={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false);
            void run('assign', () => actions.assignPosition(deal.address, to));
          }}
        >
          <span className="text">{t('confirm.transfer_position_body')}</span>
          <span className="mono" style={{ fontSize: 11, overflowWrap: 'anywhere' }}>{to}</span>
        </Sheet>
      )}

      <button
        className="btn btn-sm"
        disabled={busy !== null || to.length === 0 || !actions.connected}
        onClick={() => setConfirming(true)}
      >
        {busy === 'assign' ? '…' : t('seller.transfer_position')}
      </button>
      <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}>{t('state.close')}</button>
    </div>
  );
}

/**
 * Продажа позиции. §8
 *
 * Отличается от передачи тем, что за неё платят: цена уходит в контракт, и
 * выкупить позицию может кто угодно, заплатив её. Передача рядом остаётся —
 * это разные вещи: подарить право и продать его.
 */
function SellPosition({ deal }: { deal: DealDto }) {
  const { t } = useTranslation();
  const { sum, money } = useFmt();
  const actions = useActions();
  const { busy, waiting, err, run } = useChainAction();
  const [open, setOpen] = useState(false);
  const [price, setPrice] = useState(0);

  // null означает «эта сделка продажи не умеет»: её код развёрнут раньше,
  // чем появилась сама возможность. Предлагать такому продавцу продажу —
  // значит взять с него газ за отказ контракта.
  if (deal.askPrice === null) return null;
  const listed = BigInt(deal.askPrice) > 0n;

  if (listed) {
    return (
      <div className="col" style={{ gap: 8 }}>
        <div className="row">
          <span className="text">{t('seller.listed_for')}</span>
          <span className="mono num lime" style={{ fontWeight: 700 }}>{sum(deal.askPrice)}</span>
        </div>
        {err !== null && <div className="danger" style={{ fontSize: 12 }}>{err}</div>}
        <Waiting show={waiting} />
        <button
          className="btn btn-ghost btn-sm"
          disabled={busy !== null || !actions.connected}
          onClick={() => void run('unlist', () => actions.listPosition(deal.address, 0n))}
        >
          {busy === 'unlist' ? '…' : t('seller.unlist')}
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <button className="btn btn-ghost btn-sm" onClick={() => setOpen(true)}>
        {t('seller.sell_position')}
      </button>
    );
  }

  return (
    <div className="col" style={{ gap: 8 }}>
      <span className="text" style={{ fontSize: 11.5 }}>{t('seller.sell_position_hint')}</span>
      <div className="row">
        <span className="text">{t('seller.ask_price', { cur: CURRENCY })}</span>
        <input
          className="field"
          style={{ width: 120, textAlign: 'right' }}
          inputMode="numeric"
          value={price === 0 ? '' : String(price)}
          onChange={(e) => setPrice(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
        />
      </div>
      {err !== null && <div className="danger" style={{ fontSize: 12 }}>{err}</div>}
      <Waiting show={waiting} />
      <button
        className="btn btn-sm"
        disabled={busy !== null || price <= 0 || !actions.connected}
        onClick={() =>
          void run('list', () => actions.listPosition(deal.address, BigInt(price) * TON_UNIT))
        }
      >
        {busy === 'list' ? '…' : `${t('seller.list')} · ${sum(BigInt(price) * TON_UNIT)}`}
      </button>
      <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}>{t('state.close')}</button>
    </div>
  );
}

function SellerDeal({ deal }: { deal: DealDto }) {
  const { t } = useTranslation();
  const { money, dateTime, duration } = useFmt();
  const actions = useActions();
  const { busy, waiting, err, run } = useChainAction();
  const [confirm, setConfirm] = useState<'cancel' | 'claim' | null>(null);

  if (deal.state === 3) return <SettledDeal deal={deal} />;

  const terms = toTerms(deal);
  const paid = BigInt(deal.progress.paidTotal);
  const retained = BigInt(deal.progress.retained);
  const received = paid - retained - (paid * BigInt(terms.feeBps)) / 10000n;
  const now = Math.floor(Date.now() / 1000);
  const overdue = deal.state === 2 && isInDefault(terms, deal.progress.startTs, paid, now);
  const safeUntil = deal.state === 2 ? payBefore(terms, deal.progress.startTs, paid) : null;
  const done = stepsDone(deal, terms);
  const installment = (terms.totalPrice - terms.upfront) / BigInt(terms.nPayments);

  // Состояние 0 — сделка развёрнута, но гифт ещё у продавца: пока он не в
  // контракте, оффера для покупателя не существует.
  if (deal.state === 0) {
    return (
      <div className="card col">
        <strong className="title">{t('builder.published')}</strong>
        <span className="text">{t('builder.published_hint')}</span>
        {err !== null && <div className="danger" style={{ fontSize: 12 }}>{err}</div>}
        <Waiting show={waiting} />
        <button
          className="btn"
          disabled={busy !== null || !actions.connected}
          onClick={() =>
            void run('lock', async () => {
              // Тот же вопрос цепочке, что и в конструкторе: выставленным
              // на продажу NFT владеет контракт площадки, и перевод из
              // кошелька он отвергнет — молча и за деньги человека.
              const owner = await getNftOwner(deal.nftAddress);
              if (owner !== null && !sameAddress(owner, actions.myAddress)) {
                throw new Error(t('builder.not_owner_now'));
              }
              await actions.lockNft({ nftAddress: deal.nftAddress, dealAddress: deal.address });
            })
          }
        >
          {busy === 'lock' ? '…' : t('builder.lock_gift')}
        </button>
      </div>
    );
  }

  // Состояние 1 — гифт в залоге, ждём взнос. До взноса продавец ещё может
  // отменить сделку и забрать гифт: покупателя нет, обязательств нет.
  if (deal.state === 1) {
    return (
      <div className="card col">
        <div className="row" style={{ justifyContent: 'flex-start', gap: 12 }}>
          <Thumb image={deal.nftImage} />
          <div className="col" style={{ flex: 1, gap: 3 }}>
            <strong className="title">{t('state.waiting_upfront')}</strong>
            <span className="label">{deal.nftName ?? shortAddress(deal.nftAddress)}</span>
          </div>
        </div>
        <span className="text">{t('state.waiting_upfront_hint')}</span>
        <div className="stat">
          <div className="label label-xs">{t('offer.pay_today')}</div>
          <div className="stat-value">{money(deal.terms.upfront)}</div>
        </div>
        {err !== null && <div className="danger" style={{ fontSize: 12 }}>{err}</div>}
        <Waiting show={waiting} />
        {confirm === 'cancel' && (
          <Sheet
            title={t('confirm.cancel_deal')}
            confirmLabel={t('deal.cancel_deal')}
            busy={busy !== null}
            onClose={() => setConfirm(null)}
            onConfirm={() => {
              setConfirm(null);
              void run('cancel', () => actions.cancelDeal(deal.address));
            }}
          >
            <span className="text">{t('confirm.cancel_deal_body')}</span>
          </Sheet>
        )}
        <button
          className="btn btn-ghost btn-sm"
          disabled={busy !== null || !actions.connected}
          onClick={() => setConfirm('cancel')}
        >
          {busy === 'cancel' ? '…' : t('deal.cancel_deal')}
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Две цифры, ради которых продавец сюда заходит: что уже пришло на
          кошелёк и что удерживается до закрытия. */}
      <div className="stats2" style={{ gap: 9 }}>
        <div className="card">
          <div className="label label-xs">{t('buyer.received_wallet')}</div>
          <div className="mono num lime" style={{ fontSize: 23, fontWeight: 700, marginTop: 4 }}>
            {money(received)}
          </div>
          <div className="text" style={{ fontSize: 10.5, marginTop: 3 }}>
            {t('deals.from_upfront', {
              upfront: money(terms.upfront),
              n: done,
              each: money(installment),
            })}
          </div>
        </div>
        <div className="card">
          <div className="label label-xs">{t('deals.waiting_in_contract')}</div>
          <div className="mono num" style={{ fontSize: 23, fontWeight: 700, marginTop: 4 }}>
            {money(retained)}
          </div>
          <div className="text" style={{ fontSize: 10.5, marginTop: 3 }}>{t('deals.comes_at_closing')}</div>
        </div>
      </div>

      <div className="card col" style={{ gap: 12 }}>
        <div className="row" style={{ justifyContent: 'flex-start', gap: 12 }}>
          <Thumb image={deal.nftImage} />
          <div className="col" style={{ flex: 1, minWidth: 0, gap: 3 }}>
            <div className="title">{deal.nftName ?? shortAddress(deal.nftAddress)}</div>
            <div className="label" style={{ fontSize: 9.5, letterSpacing: 0 }}>
              {t('deals.running_level', { pct: String(refundPercentOf(terms.refundTier)) })}
            </div>
          </div>
        </div>

        <Dots done={done} total={terms.nPayments} />

        {/* График глазами продавца. Раньше на этой карточке не было ни
            одной даты: человек видел «сделка идёт» и не знал ни когда
            ближайший платёж, ни платят ли вообще. */}
        <div className="row">
          <span className="text">{t('seller.next_payment')}</span>
          <span className="mono num" style={{ fontWeight: 700 }}>
            {money(deal.progress.amountDue)}
          </span>
        </div>
        {safeUntil !== null && (
          <div className="row">
            <span className="text">{t('seller.buyer_pays_until')}</span>
            <span className={overdue ? 'mono danger' : 'mono'} style={{ fontSize: 12 }}>
              {dateTime(safeUntil)} · {overdue ? t('deals.schedule_overdue') : duration(safeUntil - now)}
            </span>
          </div>
        )}
        <div className="row">
          <span className="text">{t('deals.payment_of', { n: Math.min(done + 1, terms.nPayments), total: terms.nPayments })}</span>
          <span className="mono num" style={{ fontSize: 12 }}>
            {t('deals.paid_of', { paid: money(paid), total: money(terms.totalPrice) })}
          </span>
        </div>

        <div className="row">
          <span className="text">{t('deal.deal_total')}</span>
          <span className="mono num" style={{ fontWeight: 700 }}>{money(terms.totalPrice)}</span>
        </div>

        {err !== null && <div className="danger" style={{ fontSize: 12 }}>{err}</div>}
        <Waiting show={waiting} />

        {/* Забрать гифт можно только после льготного периода. Кнопка видна
            именно тогда: раньше контракт откажет, а человек решит, что сломано. */}
        {overdue && (
          <div className="col" style={{ gap: 6 }}>
            <span className="danger" style={{ fontSize: 12 }}>{t('seller.claim_gift_hint')}</span>
            <button
              className="btn btn-sm"
              disabled={busy !== null || !actions.connected}
              onClick={() => setConfirm('claim')}
            >
              {busy === 'claim' ? '…' : t('seller.claim_gift')}
            </button>
          </div>
        )}

        <SellPosition deal={deal} />
        <TransferPosition deal={deal} />
      </div>

      {confirm === 'claim' && (
        <Sheet
          title={t('confirm.claim_gift')}
          confirmLabel={t('seller.claim_gift')}
          busy={busy !== null}
          onClose={() => setConfirm(null)}
          onConfirm={() => {
            setConfirm(null);
            void run('claim', () => actions.claimDefault(deal.address));
          }}
        >
          <span className="text">{t('confirm.claim_gift_body')}</span>
        </Sheet>
      )}
    </>
  );
}

/**
 * Сделка, которая ждёт взноса покупателя.
 *
 * Экран «ожидание взноса» из макета, но со стороны того, кто платит: гифт уже
 * в контракте, обязательств пока ни у кого нет, и до взноса продавец волен
 * забрать гифт обратно. Поэтому здесь и срок, и прямая кнопка.
 */
function AwaitingDeal({ deal }: { deal: DealDto }) {
  const { t } = useTranslation();
  const { sum, money } = useFmt();
  const actions = useActions();
  const errorText = useErrorText();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const pay = () => {
    setBusy(true);
    setErr(null);
    void resolveInvitation(actions.myAddress)
      .then((invitation) =>
        actions.pay({
          dealAddress: deal.address,
          terms: toTerms(deal),
          startTs: deal.progress.startTs,
          exactAmount: BigInt(deal.terms.upfront),
          ...(invitation === undefined ? {} : { referral: invitation }),
        }),
      )
      .catch((e: unknown) => {
        const text = errorText(e);
        if (text.length > 0) setErr(text);
      })
      .finally(() => setBusy(false));
  };

  return (
    <div className="card col" style={{ borderColor: 'var(--lime)' }}>
      <div className="row" style={{ justifyContent: 'flex-start', gap: 12 }}>
        <Thumb image={deal.nftImage} />
        <div className="col" style={{ flex: 1, gap: 3 }}>
          <strong className="title">{t('waiting.title')}</strong>
          <span className="label">{deal.nftName ?? shortAddress(deal.nftAddress)}</span>
        </div>
      </div>
      <span className="text">{t('waiting.lead')}</span>
      <div className="stat">
        <div className="label label-xs">{t('offer.pay_today')}</div>
        <div className="stat-value">{money(deal.terms.upfront)}</div>
      </div>
      {err !== null && <div className="danger" style={{ fontSize: 12 }}>{err}</div>}
      <button className="btn" disabled={busy || !actions.connected} onClick={pay}>
        {busy ? '…' : `${t('waiting.pay')} · ${sum(deal.terms.upfront)}`}
      </button>
    </div>
  );
}

export function MyDeals({ initialSide }: { initialSide?: 'buyer' | 'seller' | undefined } = {}) {
  const { t } = useTranslation();
  const actions = useActions();
  // Сторону не выбираем за человека навсегда: пока он её не трогал, экран
  // сам показывает ту, где есть что показать. Продавец, только что
  // создавший оффер, открывал «покупателя», видел «сделок пока нет» и
  // решал, что оффер не создался.
  const [picked, setPicked] = useState<'buyer' | 'seller' | null>(initialSide ?? null);

  const { data, isLoading } = useQuery({
    queryKey: ['myDeals', actions.myAddress],
    queryFn: () => fetchWalletDeals(actions.myAddress),
    enabled: actions.connected,
  });

  // Сделки, которых в списке «моих» быть не может: покупатель попадает в
  // сделку только вместе со взносом. До взноса она известна лишь через
  // принятый запрос — и раньше человек про неё просто не узнавал.
  const awaiting = useQuery({
    queryKey: ['awaitingDeals', actions.myAddress],
    queryFn: () => fetchAwaitingDeals(actions.myAddress),
    enabled: actions.connected,
  });

  if (!actions.connected) {
    return <div className="screen"><div className="card text">{t('state.connect_wallet')}</div></div>;
  }

  const all = data?.deals ?? [];
  const asBuyer = all.filter((d) => d.buyer === actions.myAddress);
  /**
   * Со стороны продавца — только то, где сделка уже состоялась.
   *
   * До покупателя сделки как таковой нет: есть вещь, которую владелец
   * выставил или не доделал выкладку. Ей место в «Моих NFT», рядом с
   * остальными его вещами, а не во втором списке, куда он заходит смотреть
   * на идущие выплаты. Состояния 0 и 1 живут там, и отмена оффера — тоже.
   */
  const asSeller = all.filter((d) => d.seller === actions.myAddress && d.state >= 2);
  const awaitingCount = awaiting.data?.deals.length ?? 0;
  const side: 'buyer' | 'seller' =
    picked ?? (asBuyer.length + awaitingCount === 0 && asSeller.length > 0 ? 'seller' : 'buyer');
  const mine = side === 'buyer' ? asBuyer : asSeller;

  return (
    <div className="screen">
      <div className="segmented" style={{ gridTemplateColumns: '1fr 1fr' }}>
        {/* Счётчик на вкладке — чтобы «пусто» читалось как «пусто здесь», а
            не как «пусто везде»: искать свою сделку на другой вкладке
            человек не догадывается. */}
        <button aria-selected={side === 'buyer'} onClick={() => setPicked('buyer')}>
          {t('nav.deals_buyer')}
          {asBuyer.length + awaitingCount > 0 && ` · ${asBuyer.length + awaitingCount}`}
        </button>
        <button aria-selected={side === 'seller'} onClick={() => setPicked('seller')}>
          {t('nav.deals_seller')}
          {asSeller.length > 0 && ` · ${asSeller.length}`}
        </button>
      </div>

      {side === 'buyer' && awaitingCount > 0 && (
        <>
          <div className="label">{t('waiting.section')}</div>
          {awaiting.data?.deals.map((d) => <AwaitingDeal key={d.address} deal={d} />)}
        </>
      )}

      {isLoading && <div className="skeleton" style={{ minHeight: 160 }} />}
      {!isLoading && mine.length === 0 && <div className="card text">{t('deals.none')}</div>}

      {mine.map((d) =>
        side === 'buyer' ? <BuyerDeal key={d.address} deal={d} /> : <SellerDeal key={d.address} deal={d} />,
      )}
    </div>
  );
}
